/**
 * CardInput Component
 * Mobile-friendly card-based input for revenue/expense breakdown
 * Each item is displayed as a visual card with type selector and amount
 */

import React, { useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

export interface CardItem {
  type: string;
  amount: number;
}

export interface CardTypeOption {
  value: string;
  label: string;
  icon?: string;
}

interface CardInputProps {
  items: CardItem[];
  onChangeItems: (items: CardItem[]) => void;
  typeOptions: CardTypeOption[];
  maxLines?: number;
  unit?: string;
  addButtonLabel?: string;
  totalLabel?: string;
  autoRevealCount?: number; // Number of types to auto-reveal (default: 1 = no auto-reveal)
}

export default function CardInput({
  items,
  onChangeItems,
  typeOptions,
  maxLines = 6,
  unit = '€',
  addButtonLabel = '+ Ajouter une ligne',
  totalLabel = 'Total',
  autoRevealCount = 1,
}: CardInputProps) {
  const autoRevealTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoRevealTimeout.current) {
        clearTimeout(autoRevealTimeout.current);
      }
    };
  }, []);

  // Calculate total
  const total = items.reduce((sum, item) => sum + (item.amount || 0), 0);

  // Add new item
  const handleAddItem = () => {
    if (items.length >= maxLines) return;

    // Find first unused type or use 'other'
    const usedTypes = items.map(item => item.type);
    const availableType = typeOptions.find(opt => !usedTypes.includes(opt.value))?.value || 'other';

    onChangeItems([...items, { type: availableType, amount: 0 }]);
  };

  // Remove item
  const handleRemoveItem = (index: number) => {
    if (items.length <= 1) return;
    const newItems = items.filter((_, i) => i !== index);
    onChangeItems(newItems);
  };

  // Update handlers
  const handleTypeChange = (index: number, newType: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], type: newType };
    onChangeItems(newItems);
  };

  const handleAmountChange = (index: number, value: string) => {
    const numValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], amount: numValue };
    onChangeItems(newItems);
  };

  // Auto-reveal next field when user leaves the last item with a value > 0
  const handleAmountBlur = (index: number) => {
    const item = items[index];
    const isLastItem = index === items.length - 1;

    if (
      isLastItem &&
      item.amount > 0 &&
      items.length < autoRevealCount &&
      items.length < maxLines
    ) {
      if (autoRevealTimeout.current) {
        clearTimeout(autoRevealTimeout.current);
      }
      autoRevealTimeout.current = setTimeout(() => {
        const usedTypes = items.map(i => i.type);
        const nextType = typeOptions.find(opt => !usedTypes.includes(opt.value));

        if (nextType) {
          onChangeItems([...items, { type: nextType.value, amount: 0 }]);
        }
      }, 300);
    }
  };

  // Format number for display
  const formatNumber = (num: number): string => {
    if (num === 0) return '';
    return num.toLocaleString('fr-FR');
  };

  return (
    <View style={styles.container}>
      {items.map((item, index) => (
        <View key={index} style={styles.card}>
          {/* Card Header with Type Picker and Delete */}
          <View style={styles.cardHeader}>
            <View style={styles.pickerWrapper}>
              <Picker
                selectedValue={item.type}
                onValueChange={(value: string) => handleTypeChange(index, value)}
                style={styles.picker}
                itemStyle={styles.pickerItem}
              >
                {typeOptions.map((option) => (
                  <Picker.Item
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  />
                ))}
              </Picker>
            </View>
            {items.length > 1 && (
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={() => handleRemoveItem(index)}
              >
                <Text style={styles.deleteButtonText}>✕</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Card Body with Amount */}
          <View style={styles.cardBody}>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Montant mensuel</Text>
              <View style={styles.fieldInput}>
                <TextInput
                  style={styles.input}
                  value={formatNumber(item.amount)}
                  onChangeText={(value) => handleAmountChange(index, value)}
                  onBlur={() => handleAmountBlur(index)}
                  placeholder="0"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="numeric"
                />
                <Text style={styles.inputUnit}>{unit}</Text>
              </View>
            </View>
          </View>
        </View>
      ))}

      {/* Add Button */}
      {items.length < maxLines && (
        <TouchableOpacity style={styles.addButton} onPress={handleAddItem}>
          <Text style={styles.addButtonText}>{addButtonLabel}</Text>
        </TouchableOpacity>
      )}

      {/* Total Summary */}
      <View style={styles.totalContainer}>
        <Text style={styles.totalLabel}>{totalLabel}</Text>
        <Text style={styles.totalValue}>
          {total.toLocaleString('fr-FR')} {unit}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  card: {
    backgroundColor: colors.background,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.sm,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingRight: spacing.sm,
    paddingVertical: 6,
    paddingLeft: spacing.xs,
  },
  pickerWrapper: {
    flex: 1,
    height: 36,
    justifyContent: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  picker: {
    height: 36,
    width: '100%',
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingHorizontal: spacing.sm,
  },
  pickerItem: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  deleteButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.error + '15',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: spacing.sm,
  },
  deleteButtonText: {
    color: colors.error,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
  },
  cardBody: {
    padding: spacing.md,
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    flex: 1,
  },
  fieldInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    minWidth: 140,
  },
  input: {
    flex: 1,
    fontSize: typography.fontSize.md,
    color: colors.text,
    textAlign: 'right',
    paddingVertical: 4,
    minWidth: 60,
  },
  inputUnit: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
    minWidth: 20,
  },
  addButton: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.primary + '15',
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary + '30',
    borderStyle: 'dashed',
  },
  addButtonText: {
    color: colors.primary,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
  totalContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary + '30',
  },
  totalLabel: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  totalValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
  },
});
